/** Roles section of Administration → Access: list, edit, add, delete and restore workspace roles. */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, RotateCcw, ShieldCheck, Trash2 } from 'lucide-react';
import { PERMISSIONS, PERMISSION_LABELS, type Permission, type RoleDTO, type RoleUsage } from '@prism/contracts';
import { api, ApiError, errorMessage } from '../../api/client';
import { useRoles } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { TableScroll } from '../../components/TableScroll';
import { groupPermissions, usageReasons } from '../../lib/role-permissions';
import { queryKeys } from '../../api/queryKeys';

export function RolesAdmin() {
  const roles = useRoles().data?.items ?? [];
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RoleDTO | null>(null);
  const [adding, setAdding] = useState(false);
  const [blocked, setBlocked] = useState<{ role: RoleDTO; usage: RoleUsage } | null>(null);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: queryKeys.roles() }), qc.invalidateQueries({ queryKey: queryKeys.members() })]);

  async function remove(r: RoleDTO) {
    if (!window.confirm(`Delete the role “${r.name}”? Nobody uses it.`)) return;
    try {
      await api(`/api/v1/roles/${r.key}`, { method: 'DELETE' });
      toast('Role deleted');
      await refresh();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === 'ROLE_IN_USE' && e.details?.usage) setBlocked({ role: r, usage: e.details.usage as RoleUsage });
      else toast(e.message, 'error');
    }
  }
  async function restore(r: RoleDTO) {
    if (!window.confirm(`Restore “${r.name}” to its default name, description and permissions? Everyone with the role is affected on their next action.`)) return;
    try {
      await api(`/api/v1/roles/${r.key}/restore`, { method: 'POST', json: {} });
      toast('Defaults restored');
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  async function move(r: RoleDTO, dir: -1 | 1) {
    const i = roles.findIndex((x) => x.key === r.key);
    const other = roles[i + dir];
    if (!other) return;
    try {
      // Swap positions; if they are equal, nudge the moved one past the other.
      const a = r.position === other.position ? other.position + dir : other.position;
      await api(`/api/v1/roles/${r.key}`, { method: 'PATCH', json: { position: Math.max(0, a) } });
      if (r.position !== other.position) await api(`/api/v1/roles/${other.key}`, { method: 'PATCH', json: { position: r.position } });
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <section className="panel-card" style={{ marginBottom: 20 }} data-testid="roles-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ flex: 1, margin: 0 }}><ShieldCheck size={16} color="var(--accent-strong)" /> Roles</h3>
        <button className="btn sm" onClick={() => setAdding(true)} data-testid="add-role"><Plus size={14} /> Add role</button>
      </div>
      <TableScroll>
      <table className="table compact" data-testid="roles">
        <thead><tr><th>Role</th><th>Members</th><th>API keys</th><th /></tr></thead>
        <tbody>
          {roles.map((r, i) => {
            const reasons = r.usage ? usageReasons(r.usage) : [];
            const isAdminRole = r.key === 'admin';
            return (
              <tr key={r.key} data-role={r.key}>
                <td className="wrap" style={{ minWidth: 200 }}>
                  <div style={{ fontWeight: 560 }}>{r.name} {r.builtin && <span className="tag" style={{ marginLeft: 4 }}>Built-in</span>}</div>
                  {r.description && <div className="faint">{r.description}</div>}
                </td>
                <td className="mono">{r.memberCount ?? 0}</td>
                <td className="mono">{r.apiKeyCount ?? 0}</td>
                <td className="actions">
                  <button className="btn ghost icon sm" aria-label={`Move ${r.name} up`} disabled={i === 0} onClick={() => void move(r, -1)}><ArrowUp size={13} /></button>
                  <button className="btn ghost icon sm" aria-label={`Move ${r.name} down`} disabled={i === roles.length - 1} onClick={() => void move(r, 1)}><ArrowDown size={13} /></button>
                  <button className="btn ghost sm" onClick={() => setEditing(r)} data-testid="edit-role"><Pencil size={13} /> Edit</button>
                  {r.builtin && !isAdminRole && <button className="btn ghost sm" onClick={() => void restore(r)} data-testid="restore-role"><RotateCcw size={13} /> Restore defaults</button>}
                  {!isAdminRole && (
                    <button className="btn ghost sm" disabled={reasons.length > 0} onClick={() => void remove(r)} data-testid="delete-role"
                      title={reasons.length ? `In use: ${reasons.join('; ')}` : `Delete ${r.name}`}><Trash2 size={13} /> Delete</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </TableScroll>
      <p className="help" style={{ marginTop: 10 }}>Changes apply to everyone with the role on their next action. A role can be deleted only while nothing uses it. Only the Administrator role holds the Administer permission.</p>
      {editing && <RoleModal role={editing} roles={roles} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void refresh(); }} />}
      {adding && <RoleModal role={null} roles={roles} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); void refresh(); }} />}
      {blocked && (
        <Modal title={`“${blocked.role.name}” is in use`} onClose={() => setBlocked(null)} actions={<button className="btn primary" onClick={() => setBlocked(null)}>Close</button>}>
          <p className="muted" style={{ marginTop: 0 }}>A role can be deleted only when nothing uses it. Move these to another role first:</p>
          <ul data-testid="role-usage">{usageReasons(blocked.usage).map((u) => <li key={u}>{u}</li>)}</ul>
        </Modal>
      )}
    </section>
  );
}

function RoleModal({ role, roles, onClose, onSaved }: { role: RoleDTO | null; roles: RoleDTO[]; onClose: () => void; onSaved: () => void }) {
  const fixed = role?.key === 'admin';
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [copyFrom, setCopyFrom] = useState('');
  const [perms, setPerms] = useState<Permission[]>(role?.permissions ?? []);
  const [busy, setBusy] = useState(false);
  const chosenSource = roles.find((r) => r.key === copyFrom);
  const shown: Permission[] = fixed ? [...PERMISSIONS] : perms;
  const toggle = (p: Permission, on: boolean) => setPerms(on ? [...perms, p] : perms.filter((x) => x !== p));

  async function save() {
    setBusy(true);
    try {
      if (role) {
        await api(`/api/v1/roles/${role.key}`, { method: 'PATCH', json: { name: name.trim(), description, ...(fixed ? {} : { permissions: perms }) } });
        toast('Role updated');
      } else {
        await api('/api/v1/roles', { method: 'POST', json: { name: name.trim(), description, permissions: perms } });
        toast('Role added');
      }
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={role ? `Edit role: ${role.name}` : 'Add role'} onClose={onClose} wide
      actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={!name.trim() || busy} onClick={() => void save()} data-testid="save-role">{role ? 'Save' : 'Add role'}</button></>}>
      <div className="field"><label htmlFor="r-name">Name</label><input id="r-name" className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={60} /></div>
      <div className="field"><label htmlFor="r-desc">Description</label><input id="r-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} /></div>
      {!role && (
        <div className="field"><label htmlFor="r-from">Start from</label>
          <select id="r-from" className="select" value={copyFrom} onChange={(e) => {
            setCopyFrom(e.target.value);
            setPerms((roles.find((r) => r.key === e.target.value)?.permissions ?? []).filter((p) => p !== 'admin'));
          }}>
            <option value="">No permissions</option>
            {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </select>
          {chosenSource?.key === 'admin' && <span className="help">The Administer permission stays with the Administrator role and is not copied.</span>}
        </div>
      )}
      {fixed && <p className="muted">Administrators always have every permission.</p>}
      {groupPermissions(PERMISSIONS).map((g) => (
        <div className="field" key={g.title}>
          <label>{g.title}</label>
          <div className="toggle-grid">
            {g.permissions.map((p) => (
              <label key={p} className="toggle-row">
                <input type="checkbox" checked={shown.includes(p)} disabled={fixed} onChange={(e) => toggle(p, e.target.checked)} data-testid={`role-perm-${p}`} />
                <span><strong>{PERMISSION_LABELS[p].label}</strong><span className="help" style={{ display: 'block' }}>{PERMISSION_LABELS[p].help}</span></span>
              </label>
            ))}
          </div>
        </div>
      ))}
      {fixed && (
        <div className="field"><label>Administration</label>
          <div className="toggle-grid"><label className="toggle-row"><input type="checkbox" checked disabled /><span><strong>{PERMISSION_LABELS.admin.label}</strong><span className="help" style={{ display: 'block' }}>{PERMISSION_LABELS.admin.help}</span></span></label></div>
        </div>
      )}
    </Modal>
  );
}
